import { Injectable, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { NavigationStart, Router } from '@angular/router';
import { catchError, filter, switchMap, takeUntil, tap } from 'rxjs/operators';

import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { of, Subject, throwError } from 'rxjs';
import { SnackbarComponent } from '../../components/snackbar/snackbar.component';
import { TranslateService } from '@ngx-translate/core';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable({
    providedIn: 'root'
})
export class SnackbarService implements OnDestroy, HttpInterceptor {
    private readonly _destroying$ = new Subject<void>();

    constructor(
        private snackBar: MatSnackBar,
        private router: Router,
        private location: Location,
        private translate: TranslateService
    ) { }

    /**
     * Called when the component is destroyed. Unsubscribes from all subscriptions to prevent memory leaks.
     */
    ngOnDestroy() {
        this._destroying$.next();
        this._destroying$.complete();
    }

    /**
    * Intercepts HTTP requests and responses and handles them accordingly.
    * If the request method is POST, it sends the request through next.handle() and
    * intercepts the response to show a success message or an error message in a snackbar.
    * If the request method is not POST, it sends the request through next.handle() and
    * intercepts the response to show an error message in a snackbar if there is any.
    *@param {HttpRequest<any>} request - The HTTP request being intercepted.
    *@param {HttpHandler} next - The next interceptor in the chain, or the backend if no more interceptors exist.
    *@returns {Observable<HttpEvent<any>>} An observable of the HTTP response events.
    */
    intercept(request: HttpRequest<any>, next: HttpHandler) {
        if (request.method === 'POST') {
            return next.handle(request).pipe(
                takeUntil(this._destroying$),
                tap((event: HttpEvent<any>) => {
                    if (event instanceof HttpResponse) {
                        this.show('snackbarService.message.save.succes', 'snackbar-success');
                    }
                }),
                catchError((error: HttpErrorResponse) => {
                    this.show(`Erreur ${error.status}: ${error.statusText}`, 'snackbar-error');
                    return throwError(() => error);
                })
            );
        }
        if (request.method === 'PUT') {
            return next.handle(request).pipe(
                takeUntil(this._destroying$),
                tap((event: HttpEvent<any>) => {
                    if (event instanceof HttpResponse) {
                        this.show('snackbarService.message.update.succes', 'snackbar-success');
                    }
                }),
                catchError((error: HttpErrorResponse) => {
                    this.show(`Erreur ${error.status}: ${error.statusText}`, 'snackbar-error');
                    return throwError(() => error);
                })
            );
        }
        if (request.method === 'DELETE') {
            return next.handle(request).pipe(
                takeUntil(this._destroying$),
                tap((event: HttpEvent<any>) => {
                    if (event instanceof HttpResponse) {
                        this.show('snackbarService.message.delete.succes', 'snackbar-success');
                    }
                }),
                catchError((error: HttpErrorResponse) => {
                    this.show(`Erreur ${error.status}: ${error.statusText}`, 'snackbar-error');
                    return throwError(() => error);
                })
            );
        }
        else {
            return next.handle(request).pipe(
                takeUntil(this._destroying$),
                catchError((error: HttpErrorResponse) => {
                    this.show(`Erreur ${error.status}: ${error.statusText}`, 'snackbar-error');
                    return throwError(() => error);
                })
            );
        }
    }

    /**
     * Shows a snackbar with the given message and CSS class.
     * If a route is provided and the panelClass is not 'snackbar-error',
     * it navigates to the specified route after the snackbar is dismissed.
     * If no route is provided and the panelClass is not 'snackbar-error',
     * it navigates back to the previous location after the snackbar is dismissed.
     * @param {string} message - The message to display in the snackbar.
     * @param {string} panelClass - The CSS class to apply to the snackbar.
     * @param {string} route - can navigate other page if you want 
     */
    show(message: string, panelClass: string, route?: string) {
        //traduction de message 
        let messageTraduit = "";
        this.translate.get(message).subscribe((res: string) => {
            messageTraduit = res;
        });
        this.snackBar.openFromComponent(SnackbarComponent, {
            data: messageTraduit,
            panelClass: panelClass,
            duration: panelClass !== 'snackbar-error' ? 2300 : 3500
        }).afterDismissed().pipe(
            filter(() => panelClass !== 'snackbar-error'),
            switchMap(() => {
                if (route) {
                    return this.router.navigate([route]);
                } else {
                    return of(null);
                }
            }),
            takeUntil(this._destroying$)
        ).subscribe();
    }

    /**
     * Watches for changes in the router and dismisses the snackbar when a navigation event occurs.
     * This is useful to prevent the snackbar from lingering on the screen when the user navigates away from the current route.
     */
    watchRouteChange(): void {
        this.router.events.pipe(
            filter(event => event instanceof NavigationStart),
            takeUntil(this._destroying$),
            tap(() => {
                this.snackBar.dismiss();
            })
        ).subscribe();
    }
}